import { useEffect, useState } from 'react';
import { api } from './api';
import { PrimaryButton, SecondaryButton } from './FigmaComponents';
import type { FamilyState, SafetyCheckIn, SafetyEvent } from './types';

export function SafetyCenter({ familyId, userId, family, deviceId, refreshVersion = 0 }: {
  familyId: string;
  userId: string;
  family: FamilyState;
  deviceId?: string | null;
  refreshVersion?: number;
}) {
  const [events, setEvents] = useState<SafetyEvent[]>([]);
  const [checkIns, setCheckIns] = useState<SafetyCheckIn[]>([]);
  const [checkInMinutes, setCheckInMinutes] = useState(30);
  const [checkInNote, setCheckInNote] = useState('');
  const [sosConfirm, setSosConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function load() {
    const [eventResult, checkInResult] = await Promise.all([
      api<{ events: SafetyEvent[] }>(`/families/${familyId}/safety-events?limit=50`),
      api<{ checkIns: SafetyCheckIn[] }>(`/families/${familyId}/check-ins`)
    ]);
    setEvents(eventResult.events); setCheckIns(checkInResult.checkIns);
  }

  useEffect(() => { void load().catch(error => setMessage(error instanceof Error ? error.message : 'Unable to load family safety')); }, [familyId, refreshVersion]);
  useEffect(() => {
    const timer = window.setInterval(() => void load().catch(() => undefined), 30_000);
    return () => window.clearInterval(timer);
  }, [familyId]);

  async function scheduleCheckIn() {
    setBusy(true); setMessage('');
    try {
      await api(`/families/${familyId}/check-ins`, { method: 'POST', body: JSON.stringify({ dueAt: new Date(Date.now() + checkInMinutes * 60_000).toISOString(), graceMinutes: 10, note: checkInNote || undefined }) });
      setCheckInNote(''); await load(); setMessage('Safety check-in scheduled.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to schedule check-in'); }
    finally { setBusy(false); }
  }

  async function closeCheckIn(id: string, action: 'safe' | 'cancel') {
    setBusy(true); setMessage('');
    try { await api(`/check-ins/${id}/${action}`, { method: 'POST', body: '{}' }); await load(); }
    catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to update check-in'); }
    finally { setBusy(false); }
  }

  async function sendSos() {
    if (!sosConfirm) return setSosConfirm(true);
    setBusy(true); setMessage('');
    try {
      await api(`/families/${familyId}/sos`, { method: 'POST', body: JSON.stringify({ deviceId: deviceId || undefined }) });
      setSosConfirm(false); await load(); setMessage('SOS sent to your family.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to send SOS'); }
    finally { setBusy(false); }
  }

  async function updateEvent(id: string, action: 'acknowledge' | 'resolve') {
    setBusy(true); setMessage('');
    try { await api(`/safety-events/${id}/${action}`, { method: 'POST', body: '{}' }); await load(); }
    catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to update safety event'); }
    finally { setBusy(false); }
  }

  const activeOwnCheckIns = checkIns.filter(checkIn => checkIn.userId === userId && checkIn.status === 'SCHEDULED');
  const myRole = family.members.find(member => member.user.id === userId)?.role;
  return <div className="safety-center">
    {message && <div className="safety-notice">{message}</div>}
    <section className={`sos-card ${sosConfirm ? 'confirming' : ''}`}>
      <div><small>EMERGENCY</small><h2>{sosConfirm ? 'Send SOS now?' : 'Need immediate help?'}</h2><p>{sosConfirm ? 'Your family will receive a high-priority alert with your latest available location.' : 'SOS is visible, auditable and never activated silently.'}</p></div>
      <div className="sos-actions"><button disabled={busy} onClick={sendSos}>{sosConfirm ? 'Confirm SOS' : 'SOS'}</button>{sosConfirm && <SecondaryButton onClick={() => setSosConfirm(false)}>Cancel</SecondaryButton>}</div>
    </section>

    <div className="safety-grid">
      <section className="safety-card"><small>SAFETY TIMER</small><h2>Check in later</h2><p>If you do not confirm within the ten-minute grace period, your family receives a missed check-in alert.</p><label>When<select value={checkInMinutes} onChange={event => setCheckInMinutes(Number(event.target.value))}><option value={15}>15 minutes</option><option value={30}>30 minutes</option><option value={60}>1 hour</option><option value={120}>2 hours</option></select></label><label>Note<input value={checkInNote} onChange={event => setCheckInNote(event.target.value)} placeholder="Walking home, evening run…" maxLength={200}/></label><PrimaryButton disabled={busy} onClick={scheduleCheckIn}>Start safety timer</PrimaryButton>{activeOwnCheckIns.map(checkIn => <div className="active-check-in" key={checkIn.id}><div><strong>Due {new Date(checkIn.dueAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</strong><span>{checkIn.note || 'Safety check-in'}</span></div><PrimaryButton disabled={busy} onClick={() => closeCheckIn(checkIn.id, 'safe')}>I’m safe</PrimaryButton><SecondaryButton disabled={busy} onClick={() => closeCheckIn(checkIn.id, 'cancel')}>Cancel</SecondaryButton></div>)}</section>

    </div>

    <section className="safety-card event-card"><div className="section-title"><div><small>AUDITED EVENTS</small><h2>Family safety timeline</h2></div><button onClick={() => void load()}>Refresh</button></div><div className="event-list">{events.map(event => <article key={event.id} className={`event-${event.type.toLowerCase()}`}><div><strong>{event.title}</strong><span>{event.message}</span><small>{new Date(event.occurredAt).toLocaleString()} · {event.status}{event.acknowledgedBy ? ` · acknowledged by ${event.acknowledgedBy.name}` : ''}</small></div>{event.status === 'ACTIVE' && event.actorUserId !== userId && <PrimaryButton disabled={busy} onClick={() => updateEvent(event.id, 'acknowledge')}>Acknowledge</PrimaryButton>}{event.status !== 'RESOLVED' && (event.actorUserId === userId || myRole === 'OWNER' || myRole === 'ADULT') && <SecondaryButton disabled={busy} onClick={() => updateEvent(event.id, 'resolve')}>Resolve</SecondaryButton>}</article>)}{!events.length && <div className="empty-state"><strong>No safety events</strong><span>SOS, missed check-ins and place transitions will appear here.</span></div>}</div></section>
  </div>;
}
